import { Fragment, type ReactNode } from 'react';
import './richText.css';

// 评论、日报这类纯文本的排版：大家照常打字，显示时整理成好读的样子。
// - ①② / 1. / （1）/ 一、/ - / • 开头的行排成列表，编号单独一列，折行和正文对齐；
// - 空行分段；短的「xxx：」单独一行当小标题；
// - 日期（10-29）加粗，代号（AC_Soft_Start_Fault、0x02）和 `反引号` 用等宽字体，@ 的人加亮，链接可点。
// 不用正则后行断言：老版本 iOS 的微信内置浏览器不支持，会让整页报错。

type Item = { marker: string; text: string; sub: boolean };
type Block = { kind: 'p'; lines: string[] } | { kind: 'h'; text: string } | { kind: 'list'; items: Item[] };

// 「①② 请 10-29 前回复」「③–⑤ …」是在引用条目，不是新的一条
const CIRCLED = /^\s*([①-⑳])(?![①-⑳–—~～、-])\s*(.*)$/;
const ORDERED = /^\s*(\d{1,2}[.、)）](?!\d)|[（(]\d{1,2}[)）]|[一二三四五六七八九十]{1,2}、)\s*(.+)$/;
const BULLET = /^\s*([-*·•])\s+(.+)$/;
/** 像「风险点：」「单相机（竞品样机 10 月底到货）：」这样的短行；@ 开头的是在叫人，不算 */
const LABEL = /^[^\s：:@]{1}.{0,28}[：:]$/;

function parseItem(line: string): Omit<Item, 'sub'> | null {
  const m = CIRCLED.exec(line) ?? ORDERED.exec(line);
  if (m) return { marker: m[1]!, text: m[2]! };
  const b = BULLET.exec(line);
  return b ? { marker: '•', text: b[2]! } : null;
}

function parse(text: string): Block[] {
  const blocks: Block[] = [];
  let para: string[] = [];
  let list: Item[] | null = null;
  const flush = () => {
    if (para.length) blocks.push({ kind: 'p', lines: para });
    if (list) blocks.push({ kind: 'list', items: list });
    para = [];
    list = null;
  };
  for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flush();
      continue;
    }
    const item = parseItem(line);
    if (item) {
      if (para.length) {
        blocks.push({ kind: 'p', lines: para });
        para = [];
      }
      list ??= [];
      // 编号条目下面的 - 子项缩进一级
      const sub = item.marker === '•' && list.some((x) => x.marker !== '•');
      list.push({ ...item, sub });
      continue;
    }
    if (list) {
      // 缩进的行是上一条的续行；顶格的行说明列表结束了
      if (/^\s/.test(raw)) {
        list[list.length - 1]!.text += `\n${line.trim()}`;
        continue;
      }
      flush();
    }
    if (LABEL.test(line.trim()) && !para.length) {
      blocks.push({ kind: 'h', text: line.trim() });
      continue;
    }
    para.push(line);
  }
  flush();
  return blocks;
}

// 1 `代码`  2 **加粗**  3 链接  4 @某人  5 代号（带下划线的标识符、0x 十六进制）  6 日期（10-29、2026-10-29）
const INLINE =
  /`([^`\n]+)`|\*\*([^*\n]+)\*\*|(https?:\/\/[^\s，。、；）)>\]]+)|(@[^\s@，。、,.;；:：!?！？()（）]+)|([A-Za-z][A-Za-z0-9]*_[A-Za-z0-9_]*(?:\[[^\]\s]{1,12}\])?(?:=0x[0-9A-Fa-f]+|=\d+)?|0x[0-9A-Fa-f]{2,})|((?:20\d{2}-)?(?:1[0-2]|0?[1-9])-(?:[0-2]\d|3[01]))/g;
/** 日期后面跟着单位的是区间（8-12 kW、4-15kW），不当日期 */
const UNIT_AFTER = /^\s*(?:k?Wh?|kVA|V|A|%|台|套|个|块|片|mm|号|节|串)/;

function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let at = 0;
  INLINE.lastIndex = 0;
  for (let m = INLINE.exec(text); m; m = INLINE.exec(text)) {
    const before = text[m.index - 1] ?? '';
    const tail = text.slice(m.index + m[0].length);
    let node: ReactNode = null;
    if (m[1] != null) node = <code>{m[1]}</code>;
    else if (m[2] != null) node = <strong>{m[2]}</strong>;
    else if (m[3] != null)
      node = (
        <a href={m[3]} target="_blank" rel="noreferrer">
          {m[3]}
        </a>
      );
    else if (m[4] != null) node = <span className="mention">{m[4]}</span>;
    else if (m[5] != null) node = /[A-Za-z0-9_]/.test(before) ? null : <code>{m[5]}</code>;
    else if (m[6] != null) node = /[\w-]/.test(before) || /^[\w]/.test(tail) || UNIT_AFTER.test(tail) ? null : <span className="date">{m[6]}</span>;
    if (!node) continue;
    if (m.index > at) out.push(text.slice(at, m.index));
    out.push(<Fragment key={m.index}>{node}</Fragment>);
    at = m.index + m[0].length;
  }
  if (at < text.length) out.push(text.slice(at));
  return out;
}

const withBreaks = (lines: string[]) =>
  lines.map((l, i) => (
    <Fragment key={i}>
      {i > 0 && <br />}
      {inline(l)}
    </Fragment>
  ));

export function RichText({ text, className }: { text: string; className?: string }) {
  const blocks = parse(text);
  return (
    <div className={className ? `wd-rich ${className}` : 'wd-rich'}>
      {blocks.map((b, i) =>
        b.kind === 'p' ? (
          <p key={i}>{withBreaks(b.lines)}</p>
        ) : b.kind === 'h' ? (
          <p key={i} className="wd-rich-h">
            {inline(b.text)}
          </p>
        ) : (
          <div key={i} className="wd-rich-list">
            {b.items.map((it, j) =>
              it.sub ? (
                <div key={j} className="wd-rich-sub">
                  <span className="mk">•</span>
                  <span>{withBreaks(it.text.split('\n'))}</span>
                </div>
              ) : (
                <Fragment key={j}>
                  <span className="mk">{it.marker}</span>
                  <span>{withBreaks(it.text.split('\n'))}</span>
                </Fragment>
              ),
            )}
          </div>
        ),
      )}
    </div>
  );
}
